import { useCompanyWebPreview } from "@/hooks/useCompanyWebPreview";
import { companyWebTagOf } from "@/lib/company-web-tag";
import type { CompanyWebPreviewState } from "@/stores/company-web-preview-store";
import type { CompanyDTO, CompanyTag } from "@shaarp/common";
import { useState } from "react";

/**
 * The web profile of the company a page shows, filed by the server under
 * `company:<id>` (`docs/decisions/0016`): started on the first render that
 * holds the company, counted against the team's daily preview budget, free
 * when the profile is cached. Every caller on the page shares one request
 * (`companyWebPreviewStore`).
 *
 * The tag is taken once per company: the page's sections fill in its SIREN,
 * town and domain as they stream, and a tag rebuilt from each patch would key
 * a new request every time one lands.
 */
export function useCompanyPageWebProfile(company: CompanyDTO): {
  tag: CompanyTag;
  state: CompanyWebPreviewState;
  /** Asks again after a failure a new request may fix. */
  retry: () => void;
} {
  const [tag, setTag] = useState(() => companyWebTagOf(company));
  if (tag.id !== company.id) setTag(companyWebTagOf(company));
  const { state, retry } = useCompanyWebPreview(tag);
  return { tag, state, retry };
}
